import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom"; // useOutletContext をインポート
import { getAuthHeader } from "../utils/auth"; // ★ 1. 認証ヘッダー取得関数をインポート

// --- SVG Icons ---
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;

function OrdersPage() { // props から user を削除し、useOutletContext から取得
  const { user } = useOutletContext(); // user 情報を OutletContext から取得
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]); // users ステートを保持
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const API_URL = window.location.hostname === 'localhost' 
    ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
    : window.location.origin;
  const selectedClientId = useMemo(() => (user?.role === "admin" ? localStorage.getItem("selectedClientId") || "" : user?.client_id), [user]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);
  
  const fetchOrders = useCallback(() => {
    if (!selectedClientId || !user) {
      console.log("OrdersPage: selectedClientId または user が存在しないためフェッチをスキップ", { selectedClientId, user });
      return;
    }
    const params = new URLSearchParams({
      role: user.role,
      client_id: selectedClientId,
    });
    if (user.role === "staff") {
      params.set("email", user.email);
    }
    if (debouncedSearchTerm) {
      params.set("searchTerm", debouncedSearchTerm);
    }
    const url = `${API_URL}/api/orders?${params.toString()}`;
    
    console.log("OrdersPage: fetchOrders URL:", url);

    fetch(url, { headers: getAuthHeader() }) // ★ 2. 認証ヘッダーを追加
      .then(res => {
        console.log("OrdersPage: /api/orders レスポンス受信", res.status);
        return res.json();
      })
      .then(data => {
        console.log("OrdersPage: /api/orders データ:", data);
        if (data.success) {
          setOrders(data.orders || []);
        } else {
          console.error("OrdersPage: /api/orders からのデータ取得失敗:", data.error);
        }
      })
      .catch(error => {
        console.error("OrdersPage: /api/orders フェッチエラー:", error);
      });
  }, [API_URL, user, selectedClientId, debouncedSearchTerm]);

  useEffect(() => {
    fetchOrders();
    // Fetch all users to map emails to names (DealsPageと同様のロジック)
    // OrdersPageでもユーザー名を解決するために必要
    fetch(`${API_URL}/api/users?client_id=${selectedClientId}`, { headers: getAuthHeader() }) // ★ 2. 認証ヘッダーを追加
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUsers(data.users || []);
        } else {
          console.error("OrdersPage: /api/users からのデータ取得失敗:", data.error);
        }
      })
      .catch(error => {
        console.error("OrdersPage: /api/users フェッチエラー:", error);
      });
  }, [API_URL, fetchOrders, selectedClientId]); // selectedClientId も依存配列に追加

  const getStaffName = useCallback((email) => {
    const staff = users.find(u => u.email === email);
    return staff ? staff.name : email;
  }, [users]); // users ステートを依存配列に追加

  const handleDelete = (orderId) => {
    if (!window.confirm("この受注データを本当に削除しますか？")) return;
    
    fetch(`${API_URL}/api/orders/delete`, {
      method: "POST",
      headers: { // ★ 2. 認証ヘッダーを追加
        "Content-Type": "application/json",
        ...getAuthHeader()
      },
      body: JSON.stringify({ id: orderId })
    })
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        alert("受注データを削除しました。");
        fetchOrders();
      } else {
        alert("削除に失敗しました。");
      }
    });
  };

  return (
    <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">✅ 受注案件一覧</h1>
        
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
           <input 
              type="text" 
              placeholder="会社名、メモ、担当者名で検索..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border rounded-md"
            />
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <div className="divide-y divide-gray-100 md:hidden">
              {orders.length > 0 ? orders.map((order) => (
                <div key={`${order.id}-mobile`} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 break-words">{order.company}</p>
                      <p className="mt-1 text-xs text-gray-500">{new Date(order.updated_at).toLocaleDateString('ja-JP')}</p>
                    </div>
                    <button onClick={() => handleDelete(order.id)} className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100" title="削除">
                      <DeleteIcon />
                    </button>
                  </div>
                  {order.memo && <p className="mt-3 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700">{order.memo}</p>}
                  <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-500">
                    <p><span className="font-semibold text-gray-700">営業担当:</span> {getStaffName(order.sales_staff_email)}</p>
                    <p><span className="font-semibold text-gray-700">CS担当:</span> {getStaffName(order.cs_staff_email)}</p>
                    <p><span className="font-semibold text-gray-700">案件登録者:</span> {getStaffName(order.staff_email)}</p>
                  </div>
                </div>
              )) : (
                <div className="px-4 py-10 text-center text-sm text-gray-500">該当する受注案件はありません。</div>
              )}
            </div>
            <table className="hidden w-full text-sm text-left text-gray-600 md:table">
              <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="py-3 px-4">会社名</th>
                  <th className="py-3 px-4">メモ</th>
                  <th className="py-3 px-4">営業担当</th>
                  <th className="py-3 px-4">CS担当</th>
                  <th className="py-3 px-4">案件登録者</th>
                  <th className="py-3 px-4">受注日</th>
                  <th className="py-3 px-4">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {orders.length > 0 ? orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{order.company}</td>
                    <td className="py-3 px-4 whitespace-pre-wrap">{order.memo || "-"}</td>
                    <td className="py-3 px-4">{getStaffName(order.sales_staff_email)}</td>
                    <td className="py-3 px-4">{getStaffName(order.cs_staff_email)}</td>
                    <td className="py-3 px-4">{getStaffName(order.staff_email)}</td>
                    <td className="py-3 px-4">{new Date(order.updated_at).toLocaleDateString('ja-JP')}</td>
                    <td className="py-3 px-4">
                       <button onClick={() => handleDelete(order.id)} className="p-2 text-red-500 hover:bg-red-100 rounded-full" title="削除">
                        <DeleteIcon />
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7" className="text-center py-10 text-gray-500">該当する受注案件はありません。</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
           <div className="p-4 text-sm text-gray-500 border-t">
              {orders.length} 件表示中
            </div>
        </div>
      </div>
    </div>
  );
}

export default OrdersPage;
